// Order-list file tooltips (2026-08-16) — builds the native `title` strings
// for `OrderAttachmentMarker` (paperclip) and `OrderDeliveryPaperworkMarker`
// (newspaper), from the `attachment_files` / `delivery_files` arrays the
// three list endpoints attach (`server/helpers/orderFileTooltips.js`).
//
// 🚨 THIS IS A DELIBERATE CONTRACT CHANGE, NOT A NEW FEATURE BOLTED ON THE
// SIDE. Both markers used to carry NO native `title` on purpose — a `title`
// on a child wins over its parent's, and each marker's own docblock pinned
// that a child `title` would shadow the CELL's tooltip. The product owner's
// request for filename tooltips on the icons themselves REVERSES that rule:
// hovering the icon now shows file info, hovering the surrounding name/PO
// text still shows the cell's own tooltip (unaffected — this module only
// ever sets a `title` on the marker's own wrapper, never on an ancestor).
//
// ── Truncation lives HERE, not on the server ────────────────────────────
// The payload carries full filenames; this is a DISPLAY concern, so both
// builders below truncate to `MAX_TOOLTIP_NAME_LENGTH` before joining lines.

/** ~40 chars, ellipsis — keeps a multi-line native tooltip tidy. */
export const MAX_TOOLTIP_NAME_LENGTH = 40;

export function truncateFileName(name) {
  if (typeof name !== 'string' || !name) return '';
  return name.length > MAX_TOOLTIP_NAME_LENGTH
    ? `${name.slice(0, MAX_TOOLTIP_NAME_LENGTH - 1)}…`
    : name;
}

/**
 * The paperclip's multi-line `title`: `"N attachment(s):"` then one line per
 * file, labelled per `attachment_files[].label` (`null` = order-level, no
 * prefix; `'Delivery'` / `'Item N (…)'` etc. otherwise — server-computed, see
 * `orderFileTooltipFields`).
 *
 * 🪤 Tolerant of a missing/empty `files` array (a client ahead of its server,
 * or an order whose count arrived before this feature did) — still returns
 * the count line alone, never `undefined`, so the marker always carries SOME
 * title once it renders at all. Callers only invoke this when `count > 0`.
 *
 * @param {number} count
 * @param {Array<{label:?string, name:string}>|undefined} files
 * @returns {string}
 */
export function buildAttachmentTooltip(count, files) {
  const lines = [`${count} attachment${count === 1 ? '' : 's'}:`];
  (files || []).forEach((f) => {
    const name = truncateFileName(f && f.name);
    if (!name) return;
    lines.push(f.label ? `${f.label}: ${name}` : name);
  });
  return lines.join('\n');
}

/**
 * The newspaper's multi-line `title`: `"Delivery paperwork:"` then one line
 * per delivery-category filename.
 *
 * 🪤 Always returns at least the header line, even with no/empty `files` —
 * same "client ahead of its server" reasoning as `buildAttachmentTooltip`.
 * Callers only invoke this when `present === true`.
 *
 * @param {Array<string>|undefined} files
 * @returns {string}
 */
export function buildDeliveryTooltip(files) {
  const lines = ['Delivery paperwork:'];
  (files || []).forEach((name) => {
    const t = truncateFileName(name);
    if (t) lines.push(t);
  });
  return lines.join('\n');
}
